/**
 * Cookie/localStorage для pending-промо с UTM-ссылки.
 * Cookie — для server-side при желании; localStorage — основной клиентский стейт модалок.
 */

export const PROMO_STORAGE_KEY = "idealitika:promo:v1";
export const PROMO_COOKIE_NAME = "idealitika_promo_v1";
export const PROMO_COOKIE_MAX_AGE_SEC = 60 * 60 * 24 * 30; // 30 дней

export type PromoPhase = "offer" | "pending_auth" | "success" | "done";

export type StoredPromo = {
  code: string;
  phase: PromoPhase;
  capturedAt: string;
  /** Уже показали оффер (закрыли крестиком без «Попробовать»). */
  offerDismissed?: boolean;
};

function hasWindow(): boolean {
  return typeof window !== "undefined";
}

export function parseStoredPromo(raw: unknown): StoredPromo | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;
  const code = typeof obj.code === "string" ? obj.code.trim().toUpperCase() : "";
  if (!code) return null;
  const phase = obj.phase;
  if (
    phase !== "offer" &&
    phase !== "pending_auth" &&
    phase !== "success" &&
    phase !== "done"
  ) {
    return null;
  }
  return {
    code,
    phase,
    capturedAt:
      typeof obj.capturedAt === "string"
        ? obj.capturedAt
        : new Date().toISOString(),
    offerDismissed: Boolean(obj.offerDismissed),
  };
}

function writeCookie(promo: StoredPromo | null): void {
  if (!hasWindow()) return;
  if (!promo) {
    document.cookie = `${PROMO_COOKIE_NAME}=; Path=/; Max-Age=0; SameSite=Lax`;
    return;
  }
  const value = encodeURIComponent(JSON.stringify(promo));
  document.cookie = `${PROMO_COOKIE_NAME}=${value}; Path=/; Max-Age=${PROMO_COOKIE_MAX_AGE_SEC}; SameSite=Lax`;
}

export function readStoredPromo(): StoredPromo | null {
  if (!hasWindow()) return null;
  try {
    const raw = localStorage.getItem(PROMO_STORAGE_KEY);
    if (raw) {
      const parsed = parseStoredPromo(JSON.parse(raw));
      if (parsed) return parsed;
    }
  } catch {
    /* ignore */
  }
  try {
    const match = document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${PROMO_COOKIE_NAME}=`));
    if (!match) return null;
    const value = decodeURIComponent(match.slice(PROMO_COOKIE_NAME.length + 1));
    return parseStoredPromo(JSON.parse(value));
  } catch {
    return null;
  }
}

export function writeStoredPromo(promo: StoredPromo): void {
  if (!hasWindow()) return;
  try {
    localStorage.setItem(PROMO_STORAGE_KEY, JSON.stringify(promo));
  } catch {
    /* ignore */
  }
  writeCookie(promo);
}

export function clearStoredPromo(): void {
  if (!hasWindow()) return;
  try {
    localStorage.removeItem(PROMO_STORAGE_KEY);
  } catch {
    /* ignore */
  }
  writeCookie(null);
}

/** Захватить код из URL (?promo=). */
export function capturePromoFromSearch(
  search: string,
  options?: { phase?: PromoPhase },
): StoredPromo | null {
  if (!hasWindow()) return null;
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  );
  const code = params.get("promo")?.trim().toUpperCase();
  if (!code) return null;

  const preferredPhase = options?.phase ?? "offer";
  const existing = readStoredPromo();

  // Не откатывать pending_auth/success в offer при повторном ?promo= на login/register.
  if (
    existing &&
    existing.code === code &&
    !options?.phase &&
    (existing.phase === "pending_auth" || existing.phase === "success")
  ) {
    return existing;
  }

  const next: StoredPromo = {
    code,
    phase: preferredPhase,
    capturedAt: new Date().toISOString(),
    offerDismissed: preferredPhase === "pending_auth" ? true : false,
  };
  writeStoredPromo(next);
  return next;
}

export function patchStoredPromo(patch: Partial<StoredPromo>): StoredPromo | null {
  const current = readStoredPromo();
  if (!current) return null;
  const next = { ...current, ...patch, code: current.code };
  writeStoredPromo(next);
  return next;
}
